export const ANALYTICS_ENVELOPE_SCHEMA_VERSION = 1;

/**
 * `source_surface` — the origin surface stamped on every event so embed/
 * public-map traffic is separable from the signed-in app at source (the
 * embed growth loop depends on this; see `surface.ts` for the tagging seam).
 * `web_app` is the canonical name for the signed-in editor surface; `editor`
 * is kept as a back-compat alias for the M0 scaffold call sites.
 */
export type SourceSurface = "web_app" | "editor" | "public_map" | "embed" | "api" | "server";
export type Plan = "free" | "paid";

export interface AnalyticsContext {
  anonSessionId?: string;
  userId?: string;
  plan?: Plan;
  sourceSurface: SourceSurface;
}

export interface AnalyticsEnvelope {
  event: string;
  distinct_id: string;
  anon_session_id?: string;
  user_id?: string;
  plan: Plan;
  schema_version: number;
  ts: string;
  source_surface: SourceSurface;
  app_version: string;
  [propKey: string]: unknown;
}

/** A logged-in user's distinct_id is their user_id; pre-auth it's the anon_session_id — this is what keeps client/server events on the same identity. */
export function resolveDistinctId(ctx: Pick<AnalyticsContext, "userId" | "anonSessionId">): string {
  if (ctx.userId) return ctx.userId;
  if (ctx.anonSessionId) return ctx.anonSessionId;
  throw new Error("AnalyticsContext must carry a userId or anonSessionId to derive distinct_id");
}

export function buildEnvelope(
  eventName: string,
  props: Record<string, unknown>,
  ctx: AnalyticsContext,
  appVersion: string,
  now: () => string = () => new Date().toISOString(),
): AnalyticsEnvelope {
  return {
    ...props,
    event: eventName,
    distinct_id: resolveDistinctId(ctx),
    anon_session_id: ctx.anonSessionId,
    user_id: ctx.userId,
    plan: ctx.plan ?? "free",
    schema_version: ANALYTICS_ENVELOPE_SCHEMA_VERSION,
    ts: now(),
    source_surface: ctx.sourceSurface,
    app_version: appVersion,
  };
}
